import * as React from 'react';

import { faChevronLeft } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';
import { Link, useParams } from 'react-router-dom';

import SidePanel from 'src/components/sidePanel/SidePanel.tsx';
import { badgePayStationLogOptions, badgeResolvedBooleanOptions } from 'src/features/cards/utils/constants';
import { useGetPayStation } from 'src/features/payStations/api/useGetPaystation.ts';
import { useListPayStationLogs } from 'src/features/payStations/api/useListPayStationLogs';
import LogDetail from 'src/features/payStations/components/LogDetail.tsx';
import payStationsMessages from 'src/features/payStations/utils/messages';
import useFilters from 'src/hooks/useFilters';
import usePagination from 'src/hooks/usePagination';
import useSearch from 'src/hooks/useSearch';
import useSorting from 'src/hooks/useSorting';
import {
    CellBadge,
    CellDatetime,
    DataTable,
    DataTableColumnHeader,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import { CellBoolean } from 'src/registry/components/table/cells/CellBoolean';
import { INPUT_TYPE } from 'src/registry/components/table/utils/constants';
import useRowSelection from 'src/registry/hooks/useRowSelection';
import { Button } from 'src/registry/ui/Button';
import { useTitleStore } from 'src/stores/useTitleStore';

import type { PayStationLog } from 'src/features/payStations/types';

const PAYSTATION_LOGS_PANEL_NAME = 'paystation-logs-panel-panel';

const PayStationLogsTable = () => {
    const { formatMessage } = useIntl();

    const { clearRowSelection, ...rowSelection } = useRowSelection({ sidePanelName: PAYSTATION_LOGS_PANEL_NAME });
    const { queryFiltersOptions, ...filters } = useFilters();
    const { querySortingOptions, ...sorting } = useSorting({
        initialSortingState: [{ id: 'created_at', desc: true }],
    });
    const { queryPaginationOptions, ...pagination } = usePagination();
    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'logs' });

    const { payStationId } = useParams();

    const { data, isLoading, isError } = useListPayStationLogs({
        payStationId: payStationId || '',
        config: { enabled: !!payStationId },
        ...querySortingOptions,
        ...queryPaginationOptions,
        ...queryFiltersOptions,
        ...querySearchOptions,
    });

    const columnHelper = createColumnHelper<PayStationLog>();

    const { data: payStationData } = useGetPayStation({
        payStationId: payStationId || '',
        config: { enabled: !!payStationId },
    });

    const { setTitle, removeTitle } = useTitleStore();
    React.useEffect(() => {
        if (!payStationData) return;
        setTitle(payStationData.identifier);
        return () => removeTitle();
    }, [payStationData]);

    const columns = [
        columnHelper.accessor('level', {
            header: DataTableColumnHeader,
            cell: (props) => <CellBadge badgeOptions={badgePayStationLogOptions} {...props} />,
            enableSorting: true,
            enableHiding: true,
            enableColumnFilter: true,
            meta: {
                title: formatMessage(payStationsMessages.typeLog),
                badgeOptions: badgePayStationLogOptions,
            },
        }),
        columnHelper.accessor('created_at', {
            header: DataTableColumnHeader,
            cell: CellDatetime,
            enableSorting: true,
            enableColumnFilter: true,
            enableHiding: true,
            meta: {
                title: formatMessage(payStationsMessages.date),
                inputType: INPUT_TYPE.DATE,
            },
        }),
        columnHelper.accessor('message', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableHiding: true,
            enableColumnFilter: true,
            meta: {
                title: formatMessage(payStationsMessages.message),
                inputType: INPUT_TYPE.TEXT,
            },
        }),
        columnHelper.accessor('resolved', {
            header: DataTableColumnHeader,
            cell: CellBoolean,
            enableSorting: true,
            enableHiding: true,
            enableColumnFilter: true,
            meta: {
                title: formatMessage(payStationsMessages.errorResolved),
                badgeOptions: badgeResolvedBooleanOptions,
            },
        }),
        columnHelper.accessor('data.activePayment', {
            header: DataTableColumnHeader,
            cell: CellBoolean,
            enableSorting: false,
            enableHiding: true,
            enableColumnFilter: false,
            meta: {
                title: formatMessage(payStationsMessages.hasActivePayment),
            },
        }),
    ];

    const selectedRowData = React.useMemo(() => {
        const firstSelected = Object.keys(rowSelection.rowSelectionState)[0];

        if (!data || !firstSelected) return;
        return data.results.find((log) => log.id === firstSelected);
    }, [data, rowSelection.rowSelectionState]);

    return (
        <>
            <Button asChild className="-mt-6 place-self-start p-0" variant="link">
                <Link to="/pay-stations">
                    <FontAwesomeIcon icon={faChevronLeft} className="opacity-50" />
                    {formatMessage(payStationsMessages.backToAllPayStationDevices)}
                </Link>
            </Button>
            <DataTable
                columns={columns}
                data={data}
                pagination={pagination}
                sorting={sorting}
                filters={filters}
                search={search}
                semantics={{
                    title: formatMessage(payStationsMessages.logs),
                    entryName: formatMessage(payStationsMessages.log),
                    entryNames: formatMessage(payStationsMessages.logs),
                    sidePanelName: PAYSTATION_LOGS_PANEL_NAME,
                }}
                isError={isError}
                isLoading={isLoading}
                rowSelection={rowSelection}
                getRowId={(row) => row.id.toString()}
                components={{
                    controls: DataTableControls,
                    pagination: DataTablePagination,
                }}
            />
            <SidePanel onClose={clearRowSelection} name={PAYSTATION_LOGS_PANEL_NAME}>
                <LogDetail payStationLog={selectedRowData} />
            </SidePanel>
        </>
    );
};

export default PayStationLogsTable;
